// NotificationBell.js — In-app notification bell for both customer and provider dashboards
// Listens to the 'notifications' Firestore collection for the current user
// Shows a red badge with unread count; dropdown lists recent notifications
// Opening the dropdown marks all unread notifications as read

import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
} from 'firebase/firestore';

function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Subscribe to this user's notifications in real time
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown; mark all unread as read when opening
  const handleBellClick = async () => {
    const next = !showDropdown;
    setShowDropdown(next);
    if (next) {
      const unread = notifications.filter(n => !n.read);
      for (const notif of unread) {
        await updateDoc(doc(db, 'notifications', notif.id), { read: true });
      }
    }
  };

  // Converts a Firestore Timestamp to a human-readable relative string
  const getRelativeTime = (timestamp) => {
    if (!timestamp?.toDate) return '';
    const now = new Date();
    const then = timestamp.toDate();
    const seconds = Math.floor((now - then) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="notification-bell-wrapper" ref={wrapperRef}>
      <button className="btn-bell" onClick={handleBellClick}>
        🔔
        {unreadCount > 0 && (
          <span className="bell-dot">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h4>Notifications</h4>
            <button className="btn-close-notif" onClick={() => setShowDropdown(false)}>✕</button>
          </div>
          <div className="notification-list">
            {notifications.length > 0 ? (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`notification-item ${!notif.read ? 'notification-unread' : ''}`}
                >
                  <p className="notification-msg">{notif.message}</p>
                  <span className="notification-time">{getRelativeTime(notif.createdAt)}</span>
                </div>
              ))
            ) : (
              <p className="notification-empty">No notifications yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
